import { useMemo } from 'react';

import { TimeScope, TimeScopeMode } from './types';
import { adjustScopeForConstraints } from './utils';
import {
  addDays,
  addMonths,
  getDate,
  getMonth,
  getWeeksInYear,
  getYear,
  subtractDays,
  subtractMonths,
} from '../../utils/date';

export const useTimeScopeSiblings = (scope: TimeScope, mode: TimeScopeMode, minDate?: number, maxDate?: number) => {
  const nextPeriod = useMemo(() => {
    let newScope = { ...scope };

    if (mode === 'day') {
      const date = new Date(scope.year, scope.month, scope.day).valueOf();
      const nextDate = addDays(date, 1);

      newScope = adjustScopeForConstraints(
        { ...scope, year: getYear(nextDate), month: getMonth(nextDate), day: getDate(nextDate) },
        minDate,
        maxDate,
      );
    } else if (mode === 'fortnight') {
      let fortnight = scope.fortnight;
      let year = scope.year;
      let month = scope.month;

      if (fortnight === 1) {
        fortnight = 2;
      } else {
        fortnight = 1;
        const date = new Date(year, month, 1).valueOf();
        const nextDate = addMonths(date, 1);
        year = getYear(nextDate);
        month = getMonth(nextDate);
      }

      newScope = adjustScopeForConstraints({ ...scope, fortnight, month, year }, minDate, maxDate);
    } else if (mode === 'week') {
      let week = scope.week;
      let year = scope.year;
      const nbWeeks = getWeeksInYear(year);

      if (week < nbWeeks) {
        week += 1;
      } else {
        week = 1;
        year += 1;
      }

      newScope = adjustScopeForConstraints({ ...scope, week, year }, minDate, maxDate);
    } else if (mode === 'month') {
      const date = new Date(scope.year, scope.month).valueOf();
      const nextDate = addMonths(date, 1);

      newScope = adjustScopeForConstraints(
        { ...scope, year: getYear(nextDate), month: getMonth(nextDate) },
        minDate,
        maxDate,
      );
    } else if (mode === 'year') {
      newScope = adjustScopeForConstraints({ ...scope, year: scope.year + 1 }, minDate, maxDate);
    }

    return areScopesEqual(mode, newScope, scope) ? null : newScope;
  }, [maxDate, minDate, mode, scope]);

  const previousPeriod = useMemo(() => {
    let newScope = { ...scope };

    if (mode === 'day') {
      const date = new Date(scope.year, scope.month, scope.day).valueOf();
      const nextDate = subtractDays(date, 1);

      newScope = adjustScopeForConstraints(
        {
          ...scope,
          year: getYear(nextDate),
          month: getMonth(nextDate),
          day: getDate(nextDate),
        },
        minDate,
        maxDate,
      );
    } else if (mode === 'fortnight') {
      let fortnight = scope.fortnight;
      let year = scope.year;
      let month = scope.month;

      if (fortnight === 2) {
        fortnight = 1;
      } else {
        fortnight = 2;
        const date = new Date(year, month, 1).valueOf();
        const nextDate = subtractMonths(date, 1);
        year = getYear(nextDate);
        month = getMonth(nextDate);
      }

      newScope = adjustScopeForConstraints({ ...scope, fortnight, month, year }, minDate, maxDate);
    } else if (mode === 'week') {
      let week = scope.week;
      let year = scope.year;

      if (week === 1) {
        year -= 1;
        week = getWeeksInYear(year);
      } else {
        week -= 1;
      }

      newScope = adjustScopeForConstraints({ ...scope, week, year }, minDate, maxDate);
    } else if (mode === 'month') {
      const date = new Date(scope.year, scope.month).valueOf();
      const nextDate = subtractMonths(date, 1);

      newScope = adjustScopeForConstraints(
        { ...scope, year: getYear(nextDate), month: getMonth(nextDate) },
        minDate,
        maxDate,
      );
    } else if (mode === 'year') {
      newScope = adjustScopeForConstraints({ ...scope, year: scope.year - 1 }, minDate, maxDate);
    }

    return areScopesEqual(mode, newScope, scope) ? null : newScope;
  }, [maxDate, minDate, mode, scope]);

  return { nextPeriod, previousPeriod };
};

const areScopesEqual = (mode: TimeScopeMode, scope1: TimeScope, scope2: TimeScope) => {
  if (mode === 'day') {
    return scope1.year === scope2.year && scope1.month === scope2.month && scope1.day === scope2.day;
  }

  if (mode === 'fortnight') {
    return scope1.year === scope2.year && scope1.month === scope2.month && scope1.fortnight === scope2.fortnight;
  }

  if (mode === 'week') {
    return scope1.year === scope2.year && scope1.week === scope2.week;
  }

  if (mode === 'month') {
    return scope1.year === scope2.year && scope1.month === scope2.month;
  }

  if (mode === 'year') {
    return scope1.year === scope2.year;
  }

  return false;
};
